import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sprout } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const STORAGE_KEY = "agv_chat_session";

const SUGGESTIONS = [
  "How do I register for Season 1?",
  "When are the state auditions?",
  "What is the grand prize?",
  "Tell me about the partners",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null,
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          text:
            "How far! I'm Governor Bago — your AI farm assistant for the Agrovest Reality TV Show. " +
            "No wahala, I dey here to guide you. Wetin you wan know about Agrovest today?",
        },
      ]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((p) => [...p, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/chat", { message: msg, session_id: sessionId });
      if (!sessionId) {
        setSessionId(r.data.session_id);
        localStorage.setItem(STORAGE_KEY, r.data.session_id);
      }
      setMessages((p) => [...p, { role: "assistant", text: r.data.reply }]);
    } catch (err) {
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          text: "Sorry, I'm offline at the moment. Please try again shortly.",
          error: true,
        },
      ]);
      // eslint-disable-next-line no-console
      console.warn(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          onClick={() => {
            setOpen(true);
            setUnread(false);
          }}
          data-testid="chatbot-launcher"
          aria-label="Chat with Governor Bago"
          className="fixed bottom-6 right-6 z-[60] h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-[#F26A1F] hover:bg-[#D95A15] flex items-center justify-center shadow-[0_0_40px_4px_rgba(242,106,31,0.45)] transition-all hover:scale-105"
        >
          <MessageCircle size={24} className="text-white" />
          {unread && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-[#2BA84A] rounded-full ring-2 ring-black" />
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-6rem)] bg-[#0A0A0A] border border-white/15 shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-black">
            <div className="relative h-10 w-10 rounded-full bg-gradient-to-br from-[#2BA84A] to-[#1E8E3E] flex items-center justify-center shrink-0">
              <Sprout size={18} className="text-white" />
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-[#F26A1F] rounded-full ring-2 ring-black" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold tracking-tight">Governor Bago</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#2BA84A]">
                The Farmer Governor · Online
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              data-testid="chatbot-close"
              className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-white/10 hover:border-[#F26A1F] hover:text-[#F26A1F]"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-sm ${
                    m.role === "user"
                      ? "bg-[#F26A1F] text-white"
                      : m.error
                      ? "bg-red-500/15 border border-red-500/40 text-red-200"
                      : "bg-white/[0.05] border border-white/10 text-white/90"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/[0.05] border border-white/10 px-4 py-2.5 text-sm text-white/60 inline-flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#F26A1F] animate-bounce" />
                  <span className="h-2 w-2 rounded-full bg-[#F26A1F] animate-bounce [animation-delay:0.15s]" />
                  <span className="h-2 w-2 rounded-full bg-[#F26A1F] animate-bounce [animation-delay:0.3s]" />
                </div>
              </div>
            )}
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="px-3 py-1.5 text-[11px] border border-white/10 hover:border-[#F26A1F] hover:text-[#F26A1F] text-white/70 rounded-sm transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-3 border-t border-white/10 bg-black flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Governor Bago..."
              data-testid="chatbot-input"
              disabled={sending}
              className="flex-1 h-11 px-3 bg-white/[0.03] border border-white/10 focus:border-[#F26A1F] outline-none text-sm rounded-sm placeholder:text-white/30"
            />
            <button
              type="submit"
              data-testid="chatbot-send"
              disabled={sending || !input.trim()}
              className="h-11 w-11 inline-flex items-center justify-center rounded-sm bg-[#F26A1F] hover:bg-[#D95A15] disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
